import { createFeatureSelector, createSelector } from '@ngrx/store';
import { JobseekerStoreState } from '../state';
import { selectCategoriesMap, selectCurrentDate, selectLocalitiesMap, selectSystemConfigs } from '../../selectors';
import { EducationalInstitution, EducationalProgram, EducationLevelEnum, JobseekerDocument, JobseekerEducation, JobseekerEducationViewModel, JobseekerLanguage, JobseekerModerationAttributeEnum, JobseekerWorkExperience, JobseekerWorkExperienceViewModel } from '@hrra/common-models';
import { JobseekerPersonalInfoModel, JobseekerEducationModel, JobseekerLanguageModel } from 'src/app/jobseeker/shared/services/jobseeker.service';
import { BasicOptionItem, MatchType, SkillBaseTypeEnum } from '@hrra/common-models';
import { selectSpecializationsMap } from '../jobseeker-specialization/selectors';
import { selectIndustries} from '../jobseeker-industry/selectors';
import { IndustryListItem } from 'src/app/shared/models/industry-model';

export const selectJobseekerStoreState = createFeatureSelector<JobseekerStoreState>('jobseeker');

export const selectJobseeker = createSelector(
    selectJobseekerStoreState,
    j => j.jobseekerProfile.jobseeker
);

export const selectUserEmail = createSelector(
    selectJobseeker,
    j => j?.userEmail
);

export const selectJobseekerLookup = createSelector(
    selectJobseekerStoreState,
    s => s.jobseekerLookup.lookup
);

export const selectGenderList = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.genderList
);

export const selectGenderMap = createSelector(
    selectJobseekerLookup,
    e => {
        let map: {[index: number]: BasicOptionItem} ={};
        e?.genderList.reduce((a, c) => {
            a[c.key] = c
            return a;
        }, map)

        return map;
    }
);

export const selectDrivingLicenseList = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.drivingLicenceList
);

export const selectIsEmailChangeRequested = createSelector(
    selectJobseekerStoreState,
    (s) => s.jobseekerProfile.isEmailChangeRequested
);

export const selectMonthList = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.monthListFull
);

export const selectYearListBirth = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.yearListBirth
);

export const selectEducationLevels = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.educationLevels
);

export const selectEducationalInstitutions = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.educationalInstitutionsList
);

export const selectEducationalInstitutionMap = createSelector(
    selectEducationalInstitutions,
    e => {
        let map: {[index: number]: EducationalInstitution} ={};
        e?.reduce((a, c) => {
            a[c.educationalInstitutionId] = c
            return a;
        }, map)

        return map;
    }
);

export const selectDocumentTypeList = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.documentTypeList
);

export const selectDocumentTypeListMap = createSelector(
    selectJobseekerLookup,
    e => {
        let map: {[index: number]: BasicOptionItem} ={};
        e?.documentTypeList.reduce((a, c) => {
            a[c.key] = c
            return a;
        }, map)

        return map;
    }
);

export const selectLanguageProficiencyLevels = createSelector(
    selectJobseekerLookup,
    e => e.languageProficiencyLevels
);

//#region personal info
export const selectModState = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.jobseeker?.modState
);

export const nameIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.Name) || m?.includes(JobseekerModerationAttributeEnum.Surname)
    }
);

export const emailIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.Email)
    }
);

export const phoneIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.PhoneNumber) || m?.includes(JobseekerModerationAttributeEnum.PhoneNumber2)
    }
);

export const addressIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.Address)
    }
);


export const legalAddressIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.LegalAddress)
    }
);

export const extendedLocationIsInModeration = createSelector(
    selectModState,
    (m) => m?.includes(JobseekerModerationAttributeEnum.ExtendedLocationId)
);

export const genderIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.Gender)
    }
);

export const birthDateIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.DateOfBirth)
    }
);

export const currentAddressIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.Address)
    }
);

export const drivingLicenseIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.DrivingLicense)
    }
);

export const transportTypeIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.VehicleType)
    }
);

export const ownVehicleIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.IsVehicleOwner)
    }
);

export const hardSkillsIsInModeration = createSelector(
    selectModState,
    (m) => m?.includes(JobseekerModerationAttributeEnum.HardSkill)
);

export const softSkillsIsInModeration = createSelector(
    selectModState,
    (m) => m?.includes(JobseekerModerationAttributeEnum.SoftSkill)
);

export const selectJobseekerPersonalInfo = createSelector(
  selectJobseekerStoreState,

  // maps
  selectLocalitiesMap,
  selectGenderMap,

  // moderation flags
  nameIsInModeration,
  emailIsInModeration,
  phoneIsInModeration,
  addressIsInModeration,
  legalAddressIsInModeration,
  extendedLocationIsInModeration,
  genderIsInModeration,
  birthDateIsInModeration,
  currentAddressIsInModeration,
  drivingLicenseIsInModeration,
  transportTypeIsInModeration,
  ownVehicleIsInModeration,

  (
    js,
    localitiesMap,
    genderMap,
    nameModeration,
    emailModeration,
    phoneModeration,
    addressModeration,
    legalAddressModeration,
    extendedLocationModeration,
    genderModeration,
    birthDateModeration,
    currentAddressModeration,
    drivingLicenseModeration,
    transportTypeModeration,
    ownVehicleModeration
  ) => {

    const jobseeker = js.jobseekerProfile?.jobseeker;
    if (!jobseeker) return null;

    const birthDate =
      jobseeker.birthYear > 0 &&
      jobseeker.birthMonth > 0 &&
      jobseeker.birthDay > 0
        ? new Date(
            jobseeker.birthYear,
            jobseeker.birthMonth - 1,
            jobseeker.birthDay
          )
        : null;

    const genderName =
      genderMap?.[jobseeker.genderId]?.name ?? null;

    const localityName =
      localitiesMap?.[jobseeker.cityId]?.name ?? null;

    const drivingLicenses =
      (jobseeker.jobseekerDrivingLicenses ?? []).map(l => l.name);

    return {
      name: jobseeker.name,
      surname: jobseeker.surname,
      nameIsInModeration: nameModeration,

      email: jobseeker.email,
      emailIsInModeration: emailModeration,

      phone: jobseeker.phone,
      phone2: jobseeker.phone2,
      phoneIsInModeration: phoneModeration,

      gender: genderName,
      genderIsInModeration: genderModeration,

      birthDate,
      birthDateIsInModeration: birthDateModeration,

      locality: localityName,

      address: jobseeker.address,
      addressIsInModeration: addressModeration,

      legalAddress: jobseeker.legalAddress,
      legalAddressIsInModeration: legalAddressModeration,

      currentAddressIsInModeration: currentAddressModeration,

      drivingLicenses,
      drivingLicenseIsInModeration: drivingLicenseModeration,

      transportTypeIsInModeration: transportTypeModeration,
      ownVehicleIsInModeration: ownVehicleModeration,

      isVehicleOwner: jobseeker.isVehicleOwner,

      photoHref: jobseeker.photoHref,

      jobseekerVehicles: jobseeker.jobseekerVehicles ?? [],
      jobseekerExtendedLocations: jobseeker.jobseekerExtendedLocations ?? [],
      isExtendedLocationInModeration: extendedLocationModeration,
      isPendingExtendedLocation: jobseeker.isPendingExtendendLocation
    };
  }
);


export const selectPhotoIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.Photo)
    }
);

export const selectIsWorkExperienceFilledIsInModeration = createSelector(
    selectModState,
    (m) => {
        return m?.includes(JobseekerModerationAttributeEnum.IsWorkExperienceFilled)
    }
);

export const selectPhotoIsSelected = createSelector(
    selectJobseekerStoreState,
    s => s.jobseekerProfile.photoState.isPhotoSelected
);

export const selectBase64 = createSelector(
    selectJobseekerStoreState,
    s => s.jobseekerProfile.photoState?.base64
);

export const selectIsPhotoApproved = createSelector(
    selectJobseekerStoreState,
    s => s.jobseekerProfile.photoState.isPhotoApproved
);

//#endregion

export const selectJobseekerId = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.jobseeker.jobseekerId
);

//#region Jobseeke work experience
export const selectPendingNewJobseekerWorkExperiences = createSelector(
    selectJobseeker,
    e => e?.pendingNewExperiences
);


export const selectPendingModifiedJobseekerWorkExperiences = createSelector(
    selectJobseeker,
    e => e?.pendingModifiedExperiences
);

export const selectAllWorkExpericence = createSelector(
    selectJobseeker,
    selectPendingNewJobseekerWorkExperiences,
    selectPendingModifiedJobseekerWorkExperiences,
    (w, nw, mw) => {
        let result: JobseekerWorkExperience[] = [];

        w?.experiences?.forEach((c) => {
           if(!mw?.map(e => e.experienceId).includes(c.experienceId) && (!nw?.map(e => e.experienceId).includes(c.experienceId))){
               result.push(c);
           }
        });

        result = result.concat(nw, mw);
        
        result = result.sort(function(a, b){
            if (a.dateFromYear > b.dateFromYear) {
                return -1;
              }
            else if(a.dateFromYear == b.dateFromYear && a.dateFromMonth > b.dateFromMonth){
                return -1;
            }
        });
        
        return result.filter((v) => v !== undefined);
    }
);


export const selectJobseekerWorkExperiences = createSelector(
    selectJobseeker,
    selectAllWorkExpericence,
    selectPendingNewJobseekerWorkExperiences,
    selectPendingModifiedJobseekerWorkExperiences,
    selectSpecializationsMap,
    selectCurrentDate,
    (j, e, np, mp, o, d) => {
        const nowDate = new Date(d);

        if(j === null) return null;

        const result = e.map(e => {

            const x: JobseekerWorkExperienceViewModel = <any>{};
            Object.assign(x, e);

            x.isInModeration = (np?.some(c => c.experienceId == e.experienceId) || mp?.some(c => c.experienceId  == e.experienceId));

            if(e?.specializationItems){
                x.groupedSpecializations = e.specializationItems.map((e) => ({ head: e.name, subSpecializations: e.children.map((v) => { return {name:v.name}}) }))
            }

            if(e?.industryItems){
                x.groupedIndustries = e.industryItems.map((e) => ({ head: e.name, subindustries: e.children.map((v) => v.name) }))
            }


            // workExperienceDuration 
            let isCurrentYearWithoutMonth = e?.dateToMonth == null && e?.dateToYear == nowDate.getFullYear();

            let yearFrom = e?.dateFromYear;
            let monthFrom = e?.dateFromMonth != null ? (e?.dateFromMonth) : 1;
            let yearTo = 0;
            let monthTo = 0;
        
            let year: number;
            let month: number;

            if(e?.isCurrent){
                yearTo = nowDate.getFullYear();
                monthTo = nowDate.getMonth() + 1;
            }
            else{
                yearTo = e?.dateToYear;
                if(e?.dateToMonth != null){
                    monthTo = e?.dateToMonth;
                }
                else if(isCurrentYearWithoutMonth){
                    monthTo = nowDate.getMonth() + 1;
                }
                else{
                    monthTo = 12;
                }
            }

            if(yearFrom != yearTo){
                month = (12 - monthFrom) + monthTo + 1;
                year = yearTo - yearFrom - 1;
            }
            if(yearFrom == yearTo){
                month = (monthTo - monthFrom) + 1;
                year = yearTo - yearFrom;
            }
            if (month >= 12){
                year = year + (Math.floor(month/12));
                month = month % 12;
            }

            x.workExperienceDuration = {year: year, month: month};


            x.experienceDateFrom = new Date(e?.dateFromYear, e?.dateFromMonth - 1);
            x.experienceDateTo = new Date(e?.dateToYear, e?.dateToMonth - 1);
            
            return x;          
        })

        return result;
    }
);

export const selectTotalWorkExperienceMonths = createSelector(
    selectJobseeker,
    e => e?.totalWorkExperienceMonths
);

export const selectTotalWorkExperienceYears = createSelector(
    selectJobseeker,
    e => e?.totalWorkExperienceYears
);
//#endregion

// select seniority level
export const selectSelectedSeniorityLevel = createSelector(
    selectJobseekerStoreState,
    (s) => s.jobseekerProfile.workExperienceUiState.selectedSeniorityLevelId
);

// work schedule
export const selectSelectedWorkSchedule = createSelector(
    selectJobseekerStoreState,
    (s) => s.jobseekerProfile.workExperienceUiState.selectedWorkScheduleId
);

// employment form new
export const selectSelectedEmploymentFormNew = createSelector(
    selectJobseekerStoreState,
    (s) => s.jobseekerProfile.workExperienceUiState.selectedEmploymentFormNewId
);

//#region Jobseeke education
export const selectJobseekerEducations = createSelector(
    selectJobseeker,
    e => e?.jobseekerEducations
);

export const selectPendingModifiedJobseekerEducations = createSelector(
    selectJobseeker,
    e => e?.pendingModifiedJobseekerEducations
);

export const selectPendingNewJobseekerEducations = createSelector(
    selectJobseeker,
    e => e?.pendingNewJobseekerEducations
);


export const selectAllEducation = createSelector(
    selectJobseekerEducations,
    selectPendingModifiedJobseekerEducations,
    selectPendingNewJobseekerEducations,
    (w, nw, mw) => {
        let result: JobseekerEducation[] = [];

        w?.forEach((c) => {
           if(!mw.map(e => e.jobseekerEducationId).includes(c.jobseekerEducationId) && !nw.map(e => e.jobseekerEducationId).includes(c.jobseekerEducationId)){
            result.push(c);
        }
        });

        result = result.concat(nw, mw);
        
        result = result.sort(function(a, b){
            if (a.dateFromYear > b.dateFromYear) {
                return -1;
              }
            else if(a.dateFromYear == b.dateFromYear && a.dateFromMonth > b.dateFromMonth){
                return -1;
            }
        });
        
        return result;
    }
);


export const selectEducations = createSelector(
    selectAllEducation,
    selectPendingModifiedJobseekerEducations,
    selectPendingNewJobseekerEducations,
    (e, me, ne) => {
        let result = e.map(e => {
            if(e == null) return null;

            else{
                let x: JobseekerEducationViewModel = <any>{};
                Object.assign(x, e);
    
                x.isInModeration = (me?.some(c => c.jobseekerEducationId == e.jobseekerEducationId) || ne?.some(c => c.jobseekerEducationId == e.jobseekerEducationId));
    
                x.educationDateFrom = new Date(e?.dateFromYear, e?.dateFromMonth - 1);
                x.educationDateTo = new Date(e?.dateToYear, e?.dateToMonth - 1);
    
                x.hasProgram = e.educationLevel.educationLevelId == EducationLevelEnum.Bachelor || e.educationLevel.educationLevelId == EducationLevelEnum.Master || e.educationLevel.educationLevelId == EducationLevelEnum.Doctor || e.educationLevel.educationLevelId == EducationLevelEnum.College;
                x.hasSpecialization = e.educationLevel.educationLevelId == EducationLevelEnum.Bachelor || e.educationLevel.educationLevelId == EducationLevelEnum.College || e.educationLevel.educationLevelId == EducationLevelEnum.Doctor || e.educationLevel.educationLevelId == EducationLevelEnum.Master || e.educationLevel.educationLevelId == EducationLevelEnum.Other;
    
    
                x.educationLevelId = e.educationLevel.educationLevelId;
                x.educationalInstitutionId = e.educationalInstitution?.educationalInstitutionId;
                
                x.objectId = e.objectId;
                
                return x;
            }
        });

        return result;
    }
)


export const selectMaxEducationLevel = createSelector(
    selectJobseeker,
    selectJobseekerLookup,
    (e, l) => {
        let maxRank =  e?.jobseekerEducations.length > 0 ? Math.max.apply(null, e?.jobseekerEducations.map(c => c.educationLevel.rankId)) : null;
        let result = maxRank == null ? maxRank : e?.jobseekerEducations.find(e => e.educationLevel.rankId == maxRank).educationLevel.name; 
 
        return result;
    }
);
//#endregion


//#region  jobseeker languages
export const selectApprovedJobseekerLanguages = createSelector(
    selectJobseeker,
    e => e?.jobseekerLanguages
);

export const selectPendingJobseekerLanguages = createSelector(
    selectJobseeker,
    e => e?.pendingJobseekerLanguages
);

export const selectJobseekerLanguages = createSelector(
    selectApprovedJobseekerLanguages,
    selectPendingJobseekerLanguages,
    (l, pl) => {
        let result: JobseekerLanguage[] = [];
        
        l?.forEach((c) => {
            if(!pl.map(e => e.jobseekerLanguageId).includes(c.jobseekerLanguageId)){
                result.push(c);
            }
        })

        result = result.concat(pl);
        return result;
    }
);

export const selectWorldLanguageList = createSelector(
    selectJobseekerLookup,
    selectJobseekerLanguages,
    (l, lan) => {
        
        let worldLanguages = l.worldLanguages;

        let result = worldLanguages.filter(e => {
            return !lan.some(c => c.worldLanguage.worldLanguageId == e.value)
        })

        return result;
    }
)

export const languageInModeration = createSelector(
    selectApprovedJobseekerLanguages,
    selectPendingJobseekerLanguages,
    (l, pl) => {
        let map: { [index: number]: boolean } = {};
        let dict = l?.reduce((r, a) => {
            r[a.jobseekerLanguageId] = pl.some(e => e.jobseekerLanguageId == a.jobseekerLanguageId);
            return r;
        }, map)

        return dict;
    }
)
//#endregion


//#region jobseeker documents
export const selectUploadedDocuments = createSelector(
    selectJobseeker,
    e => e?.uploadedDocuments
);

export const selectpendingDocuments = createSelector(
    selectJobseeker,
    e => e?.pendingDocuments
);

export const selectJobseekerDocuments = createSelector(
    selectUploadedDocuments,
    selectpendingDocuments,
    (d, pd) => {
        let result: JobseekerDocument[] = [];

        result = result.concat(d, pd);

        return result;
    }
);

export const documentsInModeration = createSelector(
    selectJobseeker,
    selectJobseekerDocuments,
    selectpendingDocuments,
    (j, d, pd) => {
        
        if(j == null) return null;

        let map: { [index: number]: boolean } = {};
        let dict = d.reduce((r, a) => {
            r[a.jobseekerDocumentId] = pd?.some(e => e.jobseekerDocumentId == a.jobseekerDocumentId);
            return r;
        }, map)

        return dict;
    }
)
//#endregion

export const selectPersonalInfo = createSelector(
    selectJobseeker,
    j => {
        let result: JobseekerPersonalInfoModel = <any>{};

        result.name = j?.name;
        result.surname = j?.surname;
        result.email = j?.email;
        result.phone = j?.phone;
        result.phone2 = j?.phone2;
        
        result.address = j?.address;
        result.legalAddress = j?.legalAddress;
        result.sameAsCurrentAddress = j?.sameAsCurrentAddress;
        result.gender = j?.genderId;
        result.city = j?.cityId;
        result.drivingLicenses = j?.jobseekerDrivingLicenses.map(e => e.drivingLicenceId);
        result.ownVehicle = j?.isVehicleOwner;
        result.birthMonth = j?.birthMonth;
        result.birthYear = j?.birthYear;
        result.birthDay = j?.birthDay;
        result.extendedLocationPath = j?.extendedLocationPath;

        result.jobseekerVehicles = j?.jobseekerVehicles.map(c => c.vehicleTypeId);
        result.extendedLocationPath = j?.extendedLocationPath;
        result.jobseekerExtendedLocations = j?.jobseekerExtendedLocations;

        return result;
    }
);

export const selectPersonInfoHasOwnVehicle = createSelector(
    selectJobseeker,
    j => j?.isVehicleOwner
);

export const selectInstitutionSelectModeAnable = createSelector(
    selectJobseekerStoreState,
    (e) => {
        let levelId = e.jobseekerProfile.educationUiState.selectedJobseekerEducation.educationLevelId;
        return levelId == EducationLevelEnum.Bachelor || levelId == EducationLevelEnum.Master || levelId == EducationLevelEnum.Doctor
    }
);

export const selectProgramSelectModeEnable = createSelector(
    selectJobseekerStoreState,
    (e) => {
        let level = e.jobseekerProfile.educationUiState.selectedJobseekerEducation.educationLevelId;
        return level == EducationLevelEnum.Bachelor || level == EducationLevelEnum.Master
    }
);

export const selectEducation = createSelector(
    selectJobseekerStoreState,
    selectEducationalInstitutions,
    (e, i) => {
        let jobseekerEducation = e.jobseekerProfile.educationUiState.selectedJobseekerEducation;
        let x: JobseekerEducationModel = <any>{};

        x.jobseekerEducationId = jobseekerEducation?.jobseekerEducationId;
        x.educationLevelId = jobseekerEducation?.educationLevelId;
        x.educationalInstitutionId = jobseekerEducation?.educationalInstitutionId;
        x.educationalProgramId = jobseekerEducation?.educationalProgramId;
        x.educationalProgramName = jobseekerEducation?.educationalProgramName;
        x.institution = jobseekerEducation?.institution;
        x.specialization = jobseekerEducation?.specialization;
        x.dateFromYear = jobseekerEducation?.dateFromYear;
        x.dateFromMonth = jobseekerEducation?.dateFromMonth;
        x.dateToYear = jobseekerEducation?.dateToYear;
        x.dateToMonth = jobseekerEducation?.dateToMonth;
        x.description = jobseekerEducation?.description;
        x.attachment = jobseekerEducation?.attachment;
        x.objectId = jobseekerEducation?.objectId

        return x;
    }
);

export const selectHasAttachment = createSelector(
    selectJobseekerStoreState,
    (e) => {
        return e.jobseekerProfile.educationUiState.selectedJobseekerEducation?.attachment != null;
    }
)

export const selectEducationLevel = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.educationUiState.selectedJobseekerEducation?.educationLevelId
);

export const selectVehicleTypes = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerLookup.lookup?.vehicleTypes
);

export const selectIsInstitutionListAvailable = createSelector(
    selectEducationLevel,
    e => e == EducationLevelEnum.Bachelor || e == EducationLevelEnum.Doctor || e == EducationLevelEnum.Master || e == EducationLevelEnum.College
);

export const selectIsProgramListAvailable = createSelector(
    selectEducationLevel,
    e => e == EducationLevelEnum.Bachelor || e == EducationLevelEnum.Master || e == EducationLevelEnum.Doctor || e == EducationLevelEnum.College
);

export const selectInstitutionSelectMode = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.educationUiState.isInstitutionSelectModeOn
);

export const selectPrgogramSelectMode = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.educationUiState.isProgramSelectModeOn
);

export const selecIsProgramAvailable = createSelector(
    selectEducationLevel,
    e => e == EducationLevelEnum.Bachelor || e == EducationLevelEnum.Doctor || e == EducationLevelEnum.Master || e == EducationLevelEnum.College
);

export const selectIsSpecializationAvailable = createSelector(
    selectEducationLevel,
    e => e != null && e != EducationLevelEnum.HighSchool && e != EducationLevelEnum.IncompleteHighSchool
);

export const selectProgramList = createSelector(
    selectJobseekerStoreState,
    (s) => s.jobseekerLookup.lookup.educationProgramsList
);

export const selectProgramMap = createSelector(
    selectProgramList,
    e => {
        let map: {[index: number]: EducationalProgram} ={};
        e?.reduce((a, c) => {
            a[c.educationalProgramId] = c
            return a;
        }, map)

        return map;
    }
);

export const searchedInstitutionList = (searchValue: string) => createSelector(
    selectEducationalInstitutions,
    e => e.filter(e => e.name.includes(searchValue) || e.nameLatin.toLowerCase().includes(searchValue.toLowerCase()))
);

export const searchedProgramList = (searchValue: string) => createSelector(
    selectProgramList,
    e =>  e.filter(e => e.name.includes(searchValue) || e.nameLatin.toLowerCase().includes(searchValue.toLowerCase()))
);


export const selectJobseekerLanguageById = (id: number) => createSelector(
    selectJobseekerLanguages,
    e => e.find(c => c.jobseekerLanguageId == id)
);

export const selectJobseekerLanguage = (id: number) => createSelector(
    selectJobseekerLanguageById(id),
    e => {
        if(id != null){
            let x: JobseekerLanguageModel = <any>{};

            x.jobseekerLanguageId = e.jobseekerLanguageId;
            x.worldLanguageId = e.worldLanguage.worldLanguageId;
            x.languageProficiencyLevelId = e?.languageProficiencyLevel.languageProficiencyLevelId;
            x.fileName = e.attachment?.fileName
            x.name = e.worldLanguage.name;
            x.objectId = e.objectId

            return x;
       }
    }
);

export const selectJobseekerModerationLastChangeDate = createSelector(
    selectJobseeker,
    e => e.moderationLastChangeDate
)

export const languageHasAttachment = (id: number) => createSelector(
    selectJobseekerLanguageById(id),
    e => e?.attachment != null
);

export const selectFileName = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.file
);

export const selectSelectedFileText = createSelector(
    selectJobseekerStoreState,
    (e) => {
        return 'file.' + e.jobseekerProfile.file?.filename.split('.').pop()
    }
);

export const isFileUploading = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.fileUploading
);

// Specializtion
export const selectSelectedSpecializationIds = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.workExperienceUiState?.currentSelectedSpecializations
);

export const selectedSpecializations = createSelector(
    selectSelectedSpecializationIds,
    selectSpecializationsMap,
    (s, c) => {
        if(s != undefined){
            const specializations = s.map(cc => c[cc]);
    
            const map: { [index: number]: string[] } = {};
            const dict  = specializations.reduce((r, a) => {
                r[a.head?.id] = r[a.head?.id] || [];
    
                r[a.head?.id].push(a.name);
    
    
                return r;
            },map);
            const result = Object.keys(dict).map(e => ({ head: c[e]?.name, subSpecializations: dict[e] }));
    
            return result;
        }
    }
);

// Categories
export const selectSelectedCategoryIds = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.workExperienceUiState?.currentSelectedCategories
);

export const selectedCategoires = createSelector(
    selectSelectedCategoryIds,
    selectCategoriesMap,
    (s, c) => {
        if(s != undefined){
            let categories = s.map(cc => c[cc]);
    
            let map: { [index: number]: string[] } = {};
            let dict  = categories.reduce((r, a) => {
                r[a.head?.id] = r[a.head?.id] || [];
    
                r[a.head?.id].push(a.name);
    
    
                return r;
            },map);
            let result = Object.keys(dict).map(e => ({ head: c[e]?.name, subcategories: dict[e] }));
    
            return result;
        }
    }
);
export const selectSelectedIndustryIds = createSelector(
    selectJobseekerStoreState,
    s => s.jobseekerProfile.workExperienceUiState.currentSelectedIndustries
);

export const selectIndustriesMap = createSelector(
  selectIndustries,
  s => {
      const map: { [index: number]: IndustryListItem } = {};
      s.reduce((a, c) => {
          a[c.id] = c
          return a;
      }, map);
      return map;
  }
);

export const selectedIndustries = createSelector(
  selectSelectedIndustryIds,
  selectIndustriesMap,
  (s, c) => {
      if (s != undefined) {
          const industries = s.map(cc => c[cc]);

          const map: { [index: string]: string[] } = {};
          const dict = industries.reduce((r, a) => {
              if(a != undefined && a.parentId != undefined && a.parentId != null){
                      r[a.parentId] = r[a.parentId] || [];
                      r[a.parentId].push(a.name);
              }
              
              return r;
          }, map);
          const result: {head: string, subindustries: string[]}[] = Object.keys(dict).map(e => ({ head: <string>c[e]?.name, subindustries: <string[]>dict[e] }));

          return result;
      }
  }
);

export const selectJobseekerWorkExperience = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.workExperienceUiState?.workExperience
);

export const selectWorkExperienceHasAttachment = createSelector(
    selectJobseekerWorkExperience,
    e => e?.fileName != null
);

export const selectJobseekerMaxExtendedLocationCount = createSelector(
    selectSystemConfigs,
    s => s?.jobseeker.maxExperienceLocationCount
);

export const selectIsEnableExtendedLocationAddButton = createSelector(
    selectJobseekerStoreState,
    selectJobseekerMaxExtendedLocationCount,
    (s, c) => s.jobseekerProfile.workExperienceUiState.extendedLocationCounter < c
);

export const selectIsWorkExperienceFormSubmitted = createSelector(
    selectJobseekerStoreState,
    (s) => s.jobseekerProfileForAdmin.isWorkExperienceFormSubmitted
);

export const selectExtendedLocationCountLoading = createSelector(
    selectJobseekerStoreState,
    selectSystemConfigs,
    (s,c) => ({ 
        count: s.jobseekerProfileForAdmin.workExperienceUiState.extendedLocations.length, 
        loading: s.jobseekerProfileForAdmin.loading,
        maxExperienceLocationCount: c.jobseeker.maxExperienceLocationCount
    })
);

export const selectJobseekerPasswordRequirements = createSelector(
    selectSystemConfigs,
    (c) => { 
        
        if(c?.jobseeker){
            return {
                passwordRegexPattern: c.jobseeker.passwordRegexPattern,
                passwordMinLength: c.jobseeker.passwordMinLength
            }
        }
        return null;
    }
);

export const selectWorkExperienceIsCurrent = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.workExperienceUiState?.isCurrent
);

export const selectIsProfileLoading = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.loading
);

export const selectIsJobseekerProfileUpdating = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.updateLoading
);

export const selectIsFileSizeValid = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.isFileSizeValid
);

export const selectAddress = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.personalInfoState.selectedAddress
);

export const selectSameAsCurrentAddress = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.personalInfoState.selectedSameAsCurrentAddress
);

export const selectLegalAddress = createSelector(
    selectJobseekerStoreState,
    e => (e.jobseekerProfile.personalInfoState.selectedSameAsCurrentAddress) ? e.jobseekerProfile.personalInfoState.selectedAddress : e.jobseekerProfile.personalInfoState.selectedLegalAddress
);

export const selectDaysList = createSelector(
  selectJobseekerStoreState,
  (e) => {
    let size: number;

    let selectedMonth = e.jobseekerProfile.personalInfoState?.selectedMonth;
    let selectedYear = e.jobseekerProfile.personalInfoState?.selectedYear;

    let isLeapYear = selectedYear != 0 && selectedYear != null && selectedYear % 4 == 0;
    let monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

    if (selectedMonth != 0 && selectedMonth != null) {
      if (selectedMonth != 2) {
        size = monthDays[selectedMonth - 1];
      }
      if (selectedMonth == 2) {
        size = isLeapYear ? 29 : 28;
      }
    }
    else {
      size = 31;
    }

    return range(size);
  }
);

function range(size, start = 1) {
    return [...Array(size).keys()].map(i => i + start);
}


export const selectIsCvExportLoading = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.cvExportState.loading
);
//skills
export const selectJobseekerSkills = (skillBaseType: SkillBaseTypeEnum) => createSelector(
    selectJobseeker,
    j => (skillBaseType === SkillBaseTypeEnum.Hard ? j?.hardSkills : j?.softSkills) ?? []
);

export const selectSoftSkills = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.jobseeker?.softSkills
)

export const selectHardSkills = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.jobseeker?.hardSkills
)

export const selectDraftJobseekerSkills = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.skillsUiState.draftSkills
);

export const selectSkillSuggestions = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.skillsUiState.suggestions
);

export const selectSkillSuggestionsLoading = createSelector(
    selectJobseekerStoreState,
    e => e.jobseekerProfile.skillsUiState.suggestionsLoading
);

export const selectHasExactSkillMatch = createSelector(
    selectSkillSuggestions,
    s => s?.queryResultItems?.some(i => i.matchType === MatchType.ExactMatch) ?? false
);
